import { defineStore } from "pinia";
import type {
  AvailableEntity,
  ConfiguredEntity,
  DriverConnectionTestResult,
  DriverId,
  EntityDataLists,
  EntityRequest,
  IntegrationDiscoveryChangeMessage,
  IntegrationDriver,
  IntegrationDriverInfo,
  IntegrationDriverUpdate,
  IntegrationInstance,
  IntegrationRequest,
  IntegrationSetupChangeMessage,
  IntegrationSetupData,
  IntegrationSetupDataInput,
  IntegrationSetupInfo,
  IntegrationStatus,
  IntegrationStateMessage,
  NewIntegrationData,
} from "@/types/integrationInstance";
import ApiConnection from "@/api";
import type { EntityCommandMetadata } from "@/types/activity";
import {
  DeviceState,
  DriverType,
  IntegrationState,
  IntegrationDiscoveryChangeEventType,
  IntegrationSetupState,
  EntityType,
} from "@/types/enums";

import { mergeEventPayload } from "@/mixins/dataHelper";
import { createCoalescer } from "@/mixins/requestCoalescer";
import { eventRouter } from "@/api/eventRouter";
import type { NormalizedEvent } from "@/api/eventRouter";
import { appStateStore } from "@/stores/appState";

const API = ApiConnection.integrations;

/** Coalesces WS-event-triggered reloads (ws-event-handling-rework.md §3.2). */
const coalesce = createCoalescer();

/**
 * Entity types owned by this store (route registration — events for other
 * types never reach this store; processing them here caused full
 * configured-entity reloads on every activity event, REVIEW-Claude-ws-events.md
 * P0-1). "remote" is registered separately as a shared route with the remotes
 * store. Keep aligned with the EntityType enum.
 */
const INTEGRATION_ENTITY_TYPES: string[] = [
  "button",
  "climate",
  "cover",
  "light",
  "media_player",
  "sensor",
  "switch",
  "select",
];

export const integrationsStore = defineStore({
  id: "integrations",
  state: () => ({
    inited: false,
    statuses: [] as IntegrationStatus[],
    instances: [] as IntegrationInstance[],
    enabledDrivers: [] as IntegrationDriverInfo[],
    instantiableDrivers: [] as IntegrationDriverInfo[],
    // notConfiguredDrivers: [] as IntegrationDriverInfo[],
    notConfiguredCustomDrivers: [] as IntegrationDriverInfo[],
    notConfiguredExternalDrivers: [] as IntegrationDriverInfo[],
    availableEntities: [] as AvailableEntity[],
    configuredIntegrationEntities: [] as ConfiguredEntity[],
    configuredEntities: [] as ConfiguredEntity[],
    configuredSensorEntities: {
      list: [] as ConfiguredEntity[],
      fetching: false,
    },
    configuredSelectEntities: {
      list: [] as ConfiguredEntity[],
      fetching: false,
    },
    configuredEntitiesByTypes: {
      list: [],
      types: "",
    } as { list: ConfiguredEntity[]; types: string },
    commands: [] as EntityCommandMetadata[],
    discoverActive: false,
    discovered: [] as IntegrationDriverInfo[],
    availableEntitiesByPage: {
      availableEntities: [] as AvailableEntity[],
      limit: 20,
      page: 1,
      searchText: "",
      integrationId: "",
      entityTypes: "",
    },
    configuredEntitiesByPage: {
      configuredEntities: [] as ConfiguredEntity[],
      limit: 20,
      page: 1,
      searchText: "",
      integrationId: "",
      entityTypes: "",
    },
  }),

  actions: {
    init() {
      if (this.$state.inited) {
        return;
      }

      this.$state.inited = true;

      const onEntityEvent = (e: NormalizedEvent) => {
        if (e.eventType === "change" && e.entityId && e.newState) {
          this.applyEntityChange(e.entityId, e.newState);
        } else if (e.eventType === "change" && e.entityId) {
          this.reloadConfiguredEntityData(e.entityId);
        } else if (e.eventType === "new" && e.entityId && e.newState) {
          // NEW events carry the complete entity (core guarantee, see
          // docs/tasks/verify-core-new-event-completeness.md)
          this.applyEntityNew(e.entityId, e.newState as ConfiguredEntity);
        } else if (e.eventType === "delete" && e.entityId) {
          this.applyEntityDelete(e.entityId);
        } else {
          coalesce("integrations:entities", () =>
            Promise.all([
              this.getConfiguredEntities(null, true),
              this.getConfiguredEntitiesByPageByLimit(
                this.configuredEntitiesByPage.integrationId,
                true,
                this.configuredEntitiesByPage.page,
                this.configuredEntitiesByPage.limit,
                this.configuredEntitiesByPage.searchText,
                this.configuredEntitiesByPage.entityTypes
              ),
            ])
          );
        }
        this.socketUpdate(e.msgData, e);
      };
      eventRouter.route(
        "entity_change",
        INTEGRATION_ENTITY_TYPES,
        onEntityEvent,
        {
          name: "integrations",
        }
      );
      // remote entities are also configured entities → deliberate dual-owner
      // route with the remotes store (task doc §4.1)
      eventRouter.route("entity_change", "remote", onEntityEvent, {
        name: "integrations",
        shared: true,
      });

      eventRouter.route(
        "integration_driver_change",
        undefined,
        (e) => {
          // show newly created integration driver in overview screen.
          // The integration setup process might be cancelled: the driver still exists though!
          if (e.msgData.event_type === "NEW") {
            // A new driver appeared: refresh the driver lists the add-integration
            // UI renders from. Coalesced: a burst of driver events costs one round.
            coalesce("integrations:drivers", () =>
              Promise.all([
                this.getStatuses(true),
                this.getInstantiableDrivers(true),
                this.getNotConfiguredCustomDrivers(true),
                this.getNotConfiguredExternalDrivers(true),
              ]).then(() => {
                this.socketUpdate(e.msgData, e);
              })
            );
          }
        },
        { name: "integrations" }
      );

      eventRouter.route(
        "integration_change",
        undefined,
        (e) => {
          if (
            e.eventType === "change" &&
            e.newState &&
            e.msgData.integration_id
          ) {
            this.updateStatusInt(e.newState as IntegrationStatus);
            this.socketUpdate(e.msgData, e);
          } else {
            // MONITORING NOTE (ws-event-handling-rework.md OQ-6): an
            // integration_change can only invalidate statuses and instances.
            // Driver/command metadata changes only with an external driver
            // update, which the fetch-fresh-on-open behavior of the integration
            // screens covers. If integration UIs ever show stale driver/command
            // metadata after driver updates, widen this reload — do not revert
            // to reloading everything on every event.
            coalesce("integrations:change", () =>
              Promise.all([
                this.getStatuses(true),
                this.getInstances(true),
              ]).then(() => {
                this.socketUpdate(e.msgData, e);
              })
            );
          }
        },
        { name: "integrations" }
      );

      eventRouter.route(
        "integration_discovery",
        undefined,
        (e) =>
          this.integrationDiscovery(
            e.msgData as IntegrationDiscoveryChangeMessage
          ),
        { name: "integrations" }
      );
      eventRouter.route(
        "integration_setup_change",
        undefined,
        (e) =>
          this.integrationSetupChange(
            e.msgData as IntegrationSetupChangeMessage
          ),
        { name: "integrations" }
      );
      eventRouter.route(
        "integration_state",
        undefined,
        (e) => this.stateChange(e.msgData as IntegrationStateMessage),
        { name: "integrations" }
      );

      appStateStore().registerResyncHandler("integrations", () => {
        if (
          this.$state.statuses.length > 0 ||
          this.$state.instances.length > 0
        ) {
          coalesce("integrations:change", () =>
            Promise.all([this.getStatuses(true), this.getInstances(true)])
          );
        }
        if (
          this.$state.configuredEntities.length > 0 ||
          this.$state.configuredEntitiesByPage.configuredEntities.length > 0
        ) {
          coalesce("integrations:entities", () =>
            Promise.all([
              this.getConfiguredEntities(null, true),
              this.getConfiguredEntitiesByPageByLimit(
                this.configuredEntitiesByPage.integrationId,
                true,
                this.configuredEntitiesByPage.page,
                this.configuredEntitiesByPage.limit,
                this.configuredEntitiesByPage.searchText,
                this.configuredEntitiesByPage.entityTypes
              ),
            ])
          );
        }
      });
    },

    async socketUpdate(msg_data: Record<string, any>, event?: NormalizedEvent) {
      // Placeholder action for $onAction subscribers (edit screens).
      void event;
    },

    async getStatuses(update = false): Promise<IntegrationStatus[]> {
      this.init();
      if (!this.$state.statuses || !this.$state.statuses.length || update) {
        try {
          const items = await API.getIntegrationStatuses();
          this.$state.statuses = items ?? [];
          return items;
        } catch (e) {
          console.error("Failed to load integration statuses:", e);
          this.$state.statuses = [];
        }
      }
      return this.$state.statuses;
    },

    async updateStatusInt(intStatus: IntegrationStatus) {
      if (this.$state.statuses && this.$state.statuses.length > 0) {
        const existing = this.$state.statuses.find(
          (e) => e.integration_id === intStatus.integration_id
        );
        if (existing) {
          mergeEventPayload(existing, intStatus);
        } else {
          // Unknown integration: refresh the list, coalesced — a burst of
          // events must not multiply into parallel reloads (P0-2).
          coalesce("integrations:statuses", () => this.getStatuses(true));
        }
      }
    },

    async getInstances(update = false): Promise<IntegrationInstance[]> {
      this.init();
      if (!this.$state.instances || !this.$state.instances.length || update) {
        try {
          const items = await API.getInstances();
          this.$state.instances = items ?? [];
          return items;
        } catch (e) {
          console.error("Failed to load integration instances:", e);
          this.$state.instances = [];
        }
      }
      return this.$state.instances;
    },

    async getEnabledDrivers(update = false): Promise<IntegrationDriverInfo[]> {
      this.init();
      if (this.$state.discoverActive) {
        return this.$state.enabledDrivers;
      }

      if (
        !this.$state.enabledDrivers ||
        !this.$state.enabledDrivers.length ||
        update
      ) {
        try {
          const items = await API.getEnabledDrivers();
          this.$state.enabledDrivers = items ?? [];
          return items;
        } catch (e) {
          console.error("Failed to load enabled drivers:", e);
          this.$state.enabledDrivers = [];
        }
      }
      return this.$state.enabledDrivers;
    },

    async getInstantiableDrivers(
      update = false
    ): Promise<IntegrationDriverInfo[]> {
      this.init();
      if (this.$state.discoverActive) {
        return this.$state.instantiableDrivers;
      }

      if (
        !this.$state.instantiableDrivers ||
        !this.$state.instantiableDrivers.length ||
        update
      ) {
        try {
          const items = await API.getInstantiableDrivers();
          this.$state.instantiableDrivers = items ?? [];
          return items;
        } catch (e) {
          console.error("Failed to load instantiable drivers:", e);
          this.$state.instantiableDrivers = [];
        }
      }
      return this.$state.instantiableDrivers;
    },

    // async getNotConfiguredDrivers(
    //   update = false
    // ): Promise<IntegrationDriverInfo[]> {
    //   this.init();
    //   if (
    //     !this.$state.notConfiguredDrivers ||
    //     !this.$state.notConfiguredDrivers.length ||
    //     update
    //   ) {
    //     try {
    //       const items = await API.getNotConfiguredDrivers();
    //       this.$state.notConfiguredDrivers = items;
    //       return items;
    //     } catch (e) {
    //       this.$state.notConfiguredDrivers = [];
    //     }
    //   }
    //   return this.$state.notConfiguredDrivers;
    // },

    async getNotConfiguredCustomDrivers(
      update = false
    ): Promise<IntegrationDriverInfo[]> {
      this.init();
      if (
        !this.$state.notConfiguredCustomDrivers ||
        !this.$state.notConfiguredCustomDrivers.length ||
        update
      ) {
        try {
          const items = await API.getNotConfiguredCustomDrivers();
          this.$state.notConfiguredCustomDrivers = items ?? [];
          return items;
        } catch (e) {
          console.error("Failed to load not configured custom drivers:", e);
          this.$state.notConfiguredCustomDrivers = [];
        }
      }
      return this.$state.notConfiguredCustomDrivers;
    },

    async getNotConfiguredExternalDrivers(
      update = false
    ): Promise<IntegrationDriverInfo[]> {
      this.init();
      if (
        !this.$state.notConfiguredExternalDrivers ||
        !this.$state.notConfiguredExternalDrivers.length ||
        update
      ) {
        try {
          const items = await API.getNotConfiguredExternalDrivers();
          this.$state.notConfiguredExternalDrivers = items ?? [];
          return items;
        } catch (e) {
          console.error("Failed to load not configured external drivers:", e);
          this.$state.notConfiguredExternalDrivers = [];
        }
      }
      return this.$state.notConfiguredExternalDrivers;
    },

    async getCommandMetadata(update = false): Promise<EntityCommandMetadata[]> {
      if (!this.$state.commands || !this.$state.commands.length || update) {
        this.$state.commands =
          (await ApiConnection.config.getCommandData(update)) ?? [];
      }
      return this.$state.commands;
    },

    async getConfiguredEntity(entity_id: string): Promise<ConfiguredEntity> {
      this.init();
      return await API.getEntity(entity_id);
    },

    async getEntities(
      integration_id: string,
      update = false,
      user_fetch_first_page = false
    ): Promise<AvailableEntity[]> {
      this.init();
      if (
        (!this.$state.availableEntities ||
          !this.$state.availableEntities.length ||
          update) &&
        user_fetch_first_page
      ) {
        const paramFilter = "NEW";
        this.$state.availableEntities =
          (await API.getEntities(integration_id, paramFilter, true)) ?? [];
      } else if (
        !this.$state.availableEntities ||
        !this.$state.availableEntities.length ||
        update
      ) {
        const paramFilter = "NEW";
        this.$state.availableEntities =
          (await API.getEntities(integration_id, paramFilter)) ?? [];
      }
      return this.$state.availableEntities;
    },

    async getEntitiesByPage(
      integration_id: string,
      update = false,
      fetch_first_page = false,
      reload = false,
      page = 1,
      searchText = "",
      entityTypes = ""
    ): Promise<{ data: EntityDataLists; headers: object }> {
      this.init();
      let newEntities = <{ data: AvailableEntity[]; headers?: object }>{};
      if (
        (!this.$state.availableEntities ||
          !this.$state.availableEntities.length ||
          update) &&
        (fetch_first_page || reload)
      ) {
        const paramFilter = "NEW";
        newEntities = await API.getEntitiesByPage(
          integration_id,
          paramFilter,
          reload,
          page,
          searchText,
          entityTypes
        );
        this.$state.availableEntities = newEntities.data ?? [];
      } else if (
        !this.$state.availableEntities ||
        !this.$state.availableEntities.length ||
        update
      ) {
        const paramFilter = "NEW";
        newEntities = await API.getEntitiesByPage(
          integration_id,
          paramFilter,
          reload,
          page,
          searchText,
          entityTypes
        );
        this.$state.availableEntities = this.$state.availableEntities.concat(
          newEntities.data ?? []
        );
      }
      return {
        data: {
          fullList: this.$state.availableEntities,
          loadedEntityList: newEntities.data,
        },
        headers: newEntities.headers || {},
      };
    },

    async getAvailableEntitiesByPageByLimit(
      integrationId = "",
      update = false,
      page = 1,
      limit = 100,
      searchText = "",
      entityTypes = ""
    ): Promise<{
      data: {
        availableEntities: AvailableEntity[];
        limit: number;
        page: number;
      };
      headers: object;
    }> {
      this.init();
      let newEntities = <{ data: AvailableEntity[]; headers?: object }>{};
      newEntities = await API.getEntitiesByPageByLimit(
        integrationId,
        update,
        page,
        limit,
        "NEW",
        searchText,
        entityTypes
      );
      this.$state.availableEntitiesByPage.availableEntities =
        newEntities.data ?? [];
      this.$state.availableEntitiesByPage.limit = limit;
      this.$state.availableEntitiesByPage.page = page;
      this.$state.availableEntitiesByPage.searchText = searchText;
      this.$state.availableEntitiesByPage.integrationId = integrationId;
      this.$state.availableEntitiesByPage.entityTypes = entityTypes;
      return {
        data: this.$state.availableEntitiesByPage,
        headers: newEntities.headers || {},
      };
    },

    /** All lists that can cache a configured entity. */
    cachedEntityLists(): ConfiguredEntity[][] {
      return [
        this.$state.configuredEntities,
        this.$state.configuredEntitiesByPage.configuredEntities,
        this.$state.configuredSensorEntities.list,
        this.$state.configuredSelectEntities.list,
      ];
    },

    /**
     * Store contract (ws-event-handling-rework.md §4.2): merge a possibly
     * partial payload into every cached list entry, in place. Cache miss
     * updates nothing — overview pages render cached data, edit screens
     * fetch fresh via REST. (The former full reload on miss fired on EVERY
     * unknown-entity event — P0-1.)
     */
    applyEntityChange(entityId: string, patch: Record<string, any>) {
      if (patch.entity_id && patch.entity_id !== entityId) {
        return;
      }
      for (const list of this.cachedEntityLists()) {
        const cached = list.find((e) => e.entity_id === entityId);
        if (cached) {
          mergeEventPayload(cached, patch);
        }
      }
    },

    /** NEW events carry the complete entity (core guarantee, task doc OQ-2). */
    applyEntityNew(entityId: string, entity: ConfiguredEntity) {
      if (
        !this.$state.configuredEntities.some((e) => e.entity_id === entityId)
      ) {
        this.$state.configuredEntities.push(entity);
      }
      coalesce("integrations:entityPage", () =>
        this.getConfiguredEntitiesByPageByLimit(
          this.configuredEntitiesByPage.integrationId,
          true,
          this.configuredEntitiesByPage.page,
          this.configuredEntitiesByPage.limit,
          this.configuredEntitiesByPage.searchText,
          this.configuredEntitiesByPage.entityTypes
        )
      );
    },

    /** Targeted removal — no full reloads on delete (task doc P1-3). */
    applyEntityDelete(entityId: string) {
      let removedFromPage = false;
      for (const list of this.cachedEntityLists()) {
        const index = list.findIndex((e) => e.entity_id === entityId);
        if (index > -1) {
          if (
            list === this.$state.configuredEntitiesByPage.configuredEntities
          ) {
            removedFromPage = true;
          }
          list.splice(index, 1);
        }
      }
      if (
        removedFromPage ||
        this.$state.configuredEntitiesByPage.configuredEntities.length > 0
      ) {
        // total count changed → refresh pagination, coalesced
        coalesce("integrations:entityPage", () =>
          this.getConfiguredEntitiesByPageByLimit(
            this.configuredEntitiesByPage.integrationId,
            true,
            this.configuredEntitiesByPage.page,
            this.configuredEntitiesByPage.limit,
            this.configuredEntitiesByPage.searchText,
            this.configuredEntitiesByPage.entityTypes
          )
        );
      }
    },

    /** Change event without payload: reload the single entity via REST. */
    async reloadConfiguredEntityData(entityId: string) {
      const updatedEntity = await API.getEntity(entityId);
      this.applyEntityChange(updatedEntity.entity_id, updatedEntity);
    },

    async getConfiguredEntities(
      inst_id: string | null = null,
      update = false
    ): Promise<ConfiguredEntity[]> {
      this.init();
      if (
        !this.$state.configuredEntities ||
        !this.$state.configuredEntities.length ||
        update
      ) {
        this.$state.configuredEntities =
          (await API.getConfiguredEntities(inst_id)) ?? [];
      }
      return this.$state.configuredEntities;
    },

    async getConfiguredSensorEntities(
      update = false
    ): Promise<ConfiguredEntity[]> {
      this.init();
      this.$state.configuredSensorEntities.fetching = true;
      if (
        !this.$state.configuredSensorEntities ||
        !this.$state.configuredSensorEntities.list.length ||
        update
      ) {
        this.$state.configuredSensorEntities.list =
          (await API.getConfiguredEntitiesByTypes(EntityType.sensor)) ?? [];
      }
      this.$state.configuredSensorEntities.fetching = false;
      return this.$state.configuredSensorEntities.list;
    },

    async getConfiguredSelectEntities(
      update = false
    ): Promise<ConfiguredEntity[]> {
      this.init();
      this.$state.configuredSelectEntities.fetching = true;
      if (
        !this.$state.configuredSelectEntities ||
        !this.$state.configuredSelectEntities.list.length ||
        update
      ) {
        this.$state.configuredSelectEntities.list =
          (await API.getConfiguredEntitiesByTypes(EntityType.select)) ?? [];
      }
      this.$state.configuredSelectEntities.fetching = false;
      return this.$state.configuredSelectEntities.list;
    },

    async getConfiguredEntitiesByTypes(
      entityTypes: string,
      update = false
    ): Promise<{ list: ConfiguredEntity[]; types: string }> {
      this.init();
      if (
        !this.$state.configuredEntitiesByTypes.list ||
        !this.$state.configuredEntitiesByTypes.list.length ||
        this.$state.configuredEntitiesByTypes.types != entityTypes ||
        update
      ) {
        this.$state.configuredEntitiesByTypes.list =
          (await API.getConfiguredEntitiesByTypes(entityTypes)) ?? [];

        this.$state.configuredEntitiesByTypes.types = entityTypes;
      }
      return this.$state.configuredEntitiesByTypes;
    },

    async getConfiguredEntitiesByPageByLimit(
      integrationId = "",
      update = false,
      page = 1,
      limit = 100,
      searchText = "",
      entityTypes = "",
      exclude = ""
    ): Promise<{
      data: {
        configuredEntities: ConfiguredEntity[];
        limit: number;
        page: number;
      };
      headers: object;
    }> {
      this.init();
      let newEntities = <{ data: ConfiguredEntity[]; headers?: object }>{};
      newEntities = await API.getConfiguredEntitiesByPageByLimit(
        integrationId,
        update,
        page,
        limit,
        searchText,
        entityTypes,
        exclude
      );
      this.$state.configuredEntitiesByPage.configuredEntities =
        newEntities.data ?? [];
      this.$state.configuredEntitiesByPage.limit = limit;
      this.$state.configuredEntitiesByPage.page = page;
      this.$state.configuredEntitiesByPage.searchText = searchText;
      this.$state.configuredEntitiesByPage.integrationId = integrationId;
      this.$state.configuredEntitiesByPage.entityTypes = entityTypes;
      return {
        data: this.$state.configuredEntitiesByPage,
        headers: newEntities.headers || {},
      };
    },

    async getConfiguredEntitiesByPage(
      integration_id = "",
      update = false,
      fetch_first_page = false,
      reload = false,
      page = 1,
      searchText = "",
      entityTypes = ""
    ): Promise<{ data: ConfiguredEntity[]; headers: object }> {
      this.init();
      let newEntities = <{ data: ConfiguredEntity[]; headers?: object }>{};
      if (
        (!this.$state.configuredIntegrationEntities ||
          !this.$state.configuredIntegrationEntities.length ||
          update) &&
        (fetch_first_page || reload)
      ) {
        newEntities = await API.getConfiguredEntitiesByPage(
          integration_id,
          reload,
          page,
          searchText,
          entityTypes
        );
        this.$state.configuredIntegrationEntities = newEntities.data ?? [];
      } else if (
        !this.$state.configuredIntegrationEntities ||
        !this.$state.configuredIntegrationEntities.length ||
        update
      ) {
        newEntities = await API.getConfiguredEntitiesByPage(
          integration_id,
          reload,
          page,
          searchText,
          entityTypes
        );
        this.$state.configuredIntegrationEntities =
          this.$state.configuredIntegrationEntities.concat(
            newEntities.data ?? []
          );
      }
      return {
        data: this.$state.configuredIntegrationEntities,
        headers: newEntities.headers || {},
      };
    },

    async stateChange(msg_data: IntegrationStateMessage) {
      const index = this.$state.statuses.findIndex((item) => {
        return item.integration_id === msg_data.integration_id;
      });

      if (index > -1) {
        // DeviceState is a subset of IntegrationState
        this.$state.statuses[index].state = (msg_data.device_state ||
          DeviceState.UNKNOWN) as unknown as IntegrationState;
      }

      return true;
    },

    async addIntegration(
      driver_id: DriverId,
      data: NewIntegrationData
    ): Promise<IntegrationInstance> {
      return await API.addIntegration(driver_id, data);
    },

    async getIntegrationStatus(
      inst_id: string,
      update = false
    ): Promise<IntegrationStatus | undefined> {
      if (update || !this.$state.statuses || !this.$state.statuses.length) {
        await this.getStatuses(update);
      }
      return this.$state.statuses.find((intsg: IntegrationStatus) => {
        return intsg.integration_id === inst_id;
      });
    },

    async getIntegration(
      inst_id: string,
      update = false
    ): Promise<{
      inst: IntegrationInstance | null;
      status: IntegrationStatus | null;
      driver: IntegrationDriver | null;
      _error?: Record<string, any>;
    }> {
      let inst = null;
      let status = null;
      let driver = null;
      let hasError = false;
      const errors: Record<string, any> = {};
      try {
        inst = await API.getIntegration(inst_id);
      } catch (e) {
        hasError = true;
        errors.inst = e;
      }
      if (inst) {
        try {
          driver = await API.getDriver(inst.driver_id);
        } catch (e) {
          hasError = true;
          errors.driver = e;
        }
      }
      try {
        status = await this.getIntegrationStatus(inst_id, update);
      } catch (e) {
        hasError = true;
        errors.status = e;
      }
      return {
        inst,
        status: status || null,
        driver,
        _error: hasError ? errors : undefined,
      };
    },

    async updateEntityLists(
      inst_id: string,
      user_fetch_first_page = false
    ): Promise<{
      entities: AvailableEntity[];
      configured: ConfiguredEntity[];
    }> {
      const entitiesPromise = this.getEntities(
        inst_id,
        true,
        user_fetch_first_page
      );
      return entitiesPromise.then(async (entities: AvailableEntity[]) => {
        const configured = await this.getConfiguredEntities(inst_id, true);
        return {
          entities,
          configured,
        };
      });
    },

    async pagedUpdateAvailableEntityLists(
      inst_id: string,
      fetch_first_page = false,
      reload = false,
      page = 1,
      searchText = "",
      entityTypes = ""
    ): Promise<{ data: EntityDataLists; headers: any }> {
      const entities = await this.getEntitiesByPage(
        inst_id,
        true,
        fetch_first_page,
        reload,
        page,
        searchText,
        entityTypes
      );
      return entities;
    },

    async pagedUpdateConfiguredEntityLists(
      inst_id: string,
      fetch_first_page = false,
      reload = false,
      page = 1,
      searchText = "",
      entityTypes = ""
    ): Promise<{ data: ConfiguredEntity[]; headers: any }> {
      const configured = await this.getConfiguredEntitiesByPage(
        inst_id,
        true,
        fetch_first_page,
        reload,
        page,
        searchText,
        entityTypes
      );
      return configured;
    },

    async updateIntegration(
      inst_id: string,
      data: IntegrationRequest
    ): Promise<IntegrationInstance> {
      const inst = await API.updateIntegration(inst_id, data);
      this.getInstances(true);
      this.getStatuses(true);
      return inst;
    },

    async addInstanceEntity(
      inst: IntegrationInstance,
      entity: AvailableEntity
    ): Promise<ConfiguredEntity> {
      return await API.addInstanceEntity(inst, entity);
    },

    async addInstanceEntities(
      inst: IntegrationInstance,
      entity_ids: string[]
    ): Promise<string[]> {
      return await API.addInstanceEntities(inst, entity_ids);
    },

    async removeEntity(entity: ConfiguredEntity): Promise<boolean> {
      return await API.removeEntity(entity);
    },

    async removeEntities(entity_ids: string[]): Promise<boolean> {
      return await API.removeEntities(entity_ids);
    },

    async updateEntity(
      entity_id: string,
      changes: EntityRequest
    ): Promise<ConfiguredEntity> {
      const resp = await API.updateEntity(entity_id, changes);
      this.applyEntityChange(entity_id, resp);

      if (resp) {
        return resp;
      }
      return this.$state.configuredEntities.find((entity: ConfiguredEntity) => {
        return entity.entity_id === entity_id;
      }) as ConfiguredEntity;
    },

    async connectInst(inst_id: string): Promise<{
      inst: IntegrationInstance;
      status: IntegrationStatus;
    }> {
      const inst = await API.connectInst(inst_id);
      const status = await this.getIntegrationStatus(inst_id, true);
      return {
        inst,
        status: status as IntegrationStatus,
      };
    },

    async disconnectInst(inst_id: string): Promise<{
      inst: IntegrationInstance;
      status: IntegrationStatus;
    }> {
      const inst = await API.disconnectInst(inst_id);
      const status = await this.getIntegrationStatus(inst_id, true);
      return {
        inst,
        status: status as IntegrationStatus,
      };
    },

    async deleteIntegration(integration: IntegrationStatus): Promise<boolean> {
      const resp = await API.deleteIntegration(integration);
      await Promise.all([
        this.getStatuses(true),
        this.getNotConfiguredCustomDrivers(true),
        this.getNotConfiguredExternalDrivers(true),
      ]);
      return resp;
    },

    async updateDriver(
      driver_id: string,
      data: IntegrationDriverUpdate
    ): Promise<IntegrationDriver> {
      await API.updateDriver(driver_id, data);
      return API.getDriver(driver_id);
    },

    async startDiscovery(): Promise<boolean> {
      if (this.$state.discoverActive) {
        return true;
      }
      const result = await API.startDiscovery();
      this.$state.discoverActive = true;
      this.$state.discovered = [];
      return result;
    },

    async stopDiscovery(): Promise<boolean> {
      this.$state.discoverActive = false;
      this.$state.discovered = [];
      return API.stopDiscovery();
    },

    async integrationDiscovery(msg_data: IntegrationDiscoveryChangeMessage) {
      if (msg_data.event_type === IntegrationDiscoveryChangeEventType.START) {
        this.$state.discoverActive = true;
      } else if (
        msg_data.event_type === IntegrationDiscoveryChangeEventType.STOP
      ) {
        this.$state.discoverActive = false;
      } else if (
        this.$state.discoverActive &&
        msg_data.event_type === IntegrationDiscoveryChangeEventType.DISCOVERY &&
        msg_data.integration
      ) {
        this.$state.discovered.push({
          driver_id: msg_data.integration.id,
          name: {
            en: msg_data.integration.name,
          },
          driver_type: DriverType.EXTERNAL,
          driver_url: msg_data.integration.driver_url,
          pwd_protected: msg_data.integration.pwd_protected,
          version: msg_data.integration.version || "",
          icon: msg_data.integration.icon,
          developer_name: msg_data.integration.developer_name,
          discovered: true,
        });
      }
    },

    async testConnection(
      driver_id: string,
      driver_url: string,
      token: string | undefined = undefined
    ): Promise<DriverConnectionTestResult> {
      return API.testConnection(driver_id, driver_url, token);
    },

    async registerIntegration(
      driver_id: string,
      driver_url: string,
      token: string | undefined = undefined
    ): Promise<IntegrationDriver> {
      return API.registerIntegration(driver_id, driver_url, token);
    },

    async getDriver(driver_id: string): Promise<IntegrationDriver> {
      return API.getDriver(driver_id);
    },

    async startSetupIntegration(
      driver_id: string,
      reconfigure = false,
      setup_data: IntegrationSetupDataInput
    ): Promise<IntegrationSetupInfo> {
      return API.startSetupIntegration(driver_id, reconfigure, setup_data);
    },

    async continueSetupIntegration(
      driver_id: string,
      setup_data: IntegrationSetupData
    ): Promise<IntegrationSetupInfo> {
      return API.continueSetupIntegration(driver_id, setup_data);
    },

    async integrationSetupChange(data_msg: IntegrationSetupChangeMessage) {
      if (data_msg.state === IntegrationSetupState.OK) {
        await this.getInstances(true);
      }
    },

    async stopSetupIntegration(driver_id: string): Promise<boolean> {
      const response = await API.stopSetupIntegration(driver_id);
      await this.getInstances(true);
      return response;
    },
  },
});
